import { useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { useEndorsementSummaryMutations } from '@ContractBuilder/hooks/useEndorsementSummaryMutations';
import { useSectionMutations } from '@ContractBuilder/hooks/useSectionMutations';
import { useEntityStore } from '@ContractBuilder/store';
import type { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import type { ResourceSection } from '@root/@types/base';
import { isSummarySection } from '@root/helpers';
import { isEndorsementViewPath } from '@utils/app-paths';

import { useNavigation } from '../context/use-navigation';
import type { NavItem } from '../types';

import {
  calculateDragOverInsertIndex,
  determineCrossSectionDestination,
  determineTargetSectionId,
  determineWithinSectionDestination,
  findBlockInSection,
  findSectionById,
  findSectionForBlock,
  updateSectionsWithBlockMove,
} from './drag-handlers.utils';

export function useDragHandlers() {
  const { reorderBlock } = useEntityStore(({ reorderBlock }) => ({ reorderBlock }));
  const { reorderSection } = useSectionMutations();
  const { reorderSummaryBlock, reorderSummarySection } = useEndorsementSummaryMutations();

  const { navItems, sections, setActiveId, setActiveType, setIsLoading, setNavItems, setSections } = useNavigation();

  const originalNavItemsRef = useRef<NavItem[]>([]);
  const originalSectionsRef = useRef<ResourceSection[]>([]);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event;

      originalNavItemsRef.current = navItems;
      originalSectionsRef.current = sections;

      setActiveId(active.id as string);
      setActiveType(active.data.current?.type || null);
    },
    [navItems, sections, setActiveId, setActiveType],
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;

      if (!over) return;

      const activeType = active.data.current?.type;

      // Handle section reordering for live preview
      if (activeType === 'section') {
        if (active.id === over.id) return;

        // Only react when the cursor is over another section header. Resolving a
        // block hover to its parent section caused an infinite update loop when
        // multiple sections were expanded: each live reorder shifted a sibling
        // block under the cursor, dnd-kit fired dragOver again with a different
        // target, and React tripped its max update depth.
        if (over.data.current?.type !== 'section') return;

        const overNavId = over.id as string;
        if (active.id === overNavId) return;

        const oldIndex = navItems.findIndex((s) => s.id === active.id);
        const newIndex = navItems.findIndex((s) => s.id === overNavId);

        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

        const reorderedNavItems = [...navItems];
        const [movedItem] = reorderedNavItems.splice(oldIndex, 1);
        reorderedNavItems.splice(newIndex, 0, movedItem);

        setNavItems(reorderedNavItems);
        return;
      }

      if (activeType !== 'block') return;

      const activeSectionId = findSectionForBlock(sections, active.id as string);
      let overSectionId = findSectionForBlock(sections, over.id as string);

      // When hovering over a section header (not a block), use the section itself as drop target
      if (!overSectionId && over.data.current?.type === 'section') {
        const candidateSectionId = over.id as string;
        if (findSectionById(sections, candidateSectionId)) {
          overSectionId = candidateSectionId;
        }
      }

      if (!overSectionId || !activeSectionId || activeSectionId === overSectionId) return;

      if (isEndorsementViewPath()) {
        const destSection = findSectionById(sections, overSectionId);
        if (!destSection || !isSummarySection(destSection)) return;
      }

      const activeSection = findSectionById(sections, activeSectionId);
      const overSection = findSectionById(originalSectionsRef.current, overSectionId);
      const activeBlock = activeSection ? findBlockInSection(activeSection, active.id as string) : undefined;

      if (!activeSection || !overSection || !activeBlock) return;

      const insertIndex = calculateDragOverInsertIndex(
        sections,
        originalSectionsRef.current,
        active.id as string,
        over.id as string,
        overSectionId,
      );

      // Check if block is already at this position
      const modifiedOverSection = findSectionById(sections, overSectionId);
      const currentIndex = modifiedOverSection?.blocks.findIndex((b) => b.id === active.id);
      if (currentIndex === insertIndex) return;

      const updatedSections = updateSectionsWithBlockMove(
        sections,
        activeSectionId,
        overSectionId,
        activeBlock,
        insertIndex,
      );

      setSections(updatedSections);
    },
    [navItems, sections, setNavItems, setSections],
  );

  const handleSectionDragEnd = useCallback(
    async (activeId: string): Promise<boolean> => {
      const isEndorsementSummary = isEndorsementViewPath();

      const data = isEndorsementSummary ? navItems : sections;
      const ref = isEndorsementSummary ? originalNavItemsRef : originalSectionsRef;

      const newIndex = data.findIndex((s) => s.id === activeId);
      const originalIndex = ref.current.findIndex((s) => s.id === activeId);

      if (newIndex === -1 || originalIndex === -1 || newIndex === originalIndex) return false;

      if (isEndorsementSummary) {
        await reorderSummarySection.mutateAsync({
          sectionId: activeId,
          insertBeforeId: navItems[newIndex + 1]?.id ?? null,
        });
      } else {
        await reorderSection.mutateAsync({ sectionId: activeId, destinationIndex: newIndex });
      }

      return true;
    },
    [navItems, sections, reorderSection, reorderSummarySection],
  );

  const handleBlockDragEnd = useCallback(
    async (activeId: string, overId: string, over: DragEndEvent['over']): Promise<boolean> => {
      if (!over) return false;

      const originalSourceSectionId = findSectionForBlock(originalSectionsRef.current, activeId);

      if (!originalSourceSectionId) return false;

      const dropTargetType = over.data.current?.type;
      const dropTargetSectionId = over.data.current?.sectionId;

      const targetSectionId = determineTargetSectionId(overId, dropTargetType, dropTargetSectionId);

      if (!targetSectionId) return false;

      const isCrossSectionMove = originalSourceSectionId !== targetSectionId;

      if (!isCrossSectionMove && activeId === overId) return false;

      if (isEndorsementViewPath() && isCrossSectionMove) {
        const destSection = findSectionById(originalSectionsRef.current, targetSectionId);
        if (!destSection || !isSummarySection(destSection)) return false;
      }

      const targetSection = findSectionById(originalSectionsRef.current, targetSectionId);
      if (!targetSection) return false;

      let destinationIndex = -1;

      if (isCrossSectionMove) {
        destinationIndex = determineCrossSectionDestination(targetSection, dropTargetType, overId, activeId);
      } else {
        if (dropTargetType === 'section') return false;
        if (dropTargetType === 'block') {
          destinationIndex = determineWithinSectionDestination(targetSection, overId);
        }
      }

      if (destinationIndex === -1) return false;

      if (isEndorsementViewPath()) {
        await reorderSummaryBlock.mutateAsync({
          blockId: activeId,
          order: destinationIndex,
          ...(isCrossSectionMove && { targetSectionId }),
        });
      } else {
        await reorderBlock(activeId, destinationIndex, isCrossSectionMove ? targetSectionId : undefined);
      }

      return true;
    },
    [reorderBlock, reorderSummaryBlock],
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) {
        setActiveId(null);
        setActiveType(null);
        setNavItems(originalNavItemsRef.current);
        return;
      }

      const activeType = active.data.current?.type;

      try {
        // Spurious event from component remount during optimistic update — return inside try
        // so the finally block still runs and resets activeId/activeType.
        if (activeType !== 'section' && activeType !== 'block') return;

        setIsLoading(true);

        if (activeType === 'section') {
          const changed = await handleSectionDragEnd(active.id as string);
          if (!changed) setNavItems(originalNavItemsRef.current);
        } else if (activeType === 'block') {
          const moved = await handleBlockDragEnd(active.id as string, over.id as string, over);
          if (!moved) setNavItems(originalNavItemsRef.current);
        }
      } catch (_error) {
        toast.error('Failed to reorder. Please try again.');
        setNavItems(originalNavItemsRef.current);
      } finally {
        setIsLoading(false);
        setActiveId(null);
        setActiveType(null);
      }
    },
    [handleSectionDragEnd, handleBlockDragEnd, setActiveId, setActiveType, setIsLoading, setNavItems],
  );

  return {
    handleDragStart,
    handleDragOver,
    handleDragEnd,
  };
}
